import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../shared/api/client";
import type {
  EnvironmentView,
  ExecutionPhase,
  ExecutionStepType,
  OnFailureAction,
  ReleaseDetail,
  ReleaseExecutionStep,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";
import { EXECUTION_PHASE_LABELS, ON_FAILURE_LABELS } from "./releaseVocabulary";

const STEP_TYPES: ExecutionStepType[] = [
  "MANUAL", "SCRIPT", "PIPELINE", "DATABASE", "CONFIGURATION",
  "INFRASTRUCTURE", "VALIDATION", "COMMUNICATION", "OTHER",
];

/**
 * Writing one step of the runbook (§32, §33, §35).
 *
 * <p>The instruction field carries a standing warning about secrets, and the server refuses
 * anything that looks like a pasted credential. It is worth the noise: this text is read by
 * everyone with release access, kept for years, and outlives every rotation policy — a password
 * written here is a password that has escaped.
 */
export function ExecutionStepDrawer({
  release,
  step,
  leg,
  allSteps,
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  step: ReleaseExecutionStep | null;
  /** The environment being worked on, when one leg is open — new steps default here. */
  leg?: EnvironmentView | null;
  allSteps: ReleaseExecutionStep[];
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = step != null;
  const [environmentId, setEnvironmentId] = useState("");

  /* §63: a runbook is rarely one list. Three steps put a release on a sandbox; fifteen put the
     same release into production, and judging one by the other reports it wrongly. */
  const environments = useQuery({
    queryKey: ["release-environments"],
    queryFn: () => api.get<EnvironmentView[]>("/api/v1/environments"),
    enabled: open,
  });

  const [phase, setPhase] = useState<ExecutionPhase>("DEPLOY");
  const [title, setTitle] = useState("");
  const [instruction, setInstruction] = useState("");
  const [stepType, setStepType] = useState<ExecutionStepType>("MANUAL");
  const [component, setComponent] = useState("");
  const [mandatory, setMandatory] = useState(true);
  const [evidenceRequired, setEvidenceRequired] = useState(false);
  const [onFailure, setOnFailure] = useState<OnFailureAction>("STOP_RELEASE");
  const [duration, setDuration] = useState("");
  const [dependsOn, setDependsOn] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (!open) {
      return;
    }
    setPhase(step?.phase ?? "DEPLOY");
    setTitle(step?.title ?? "");
    setInstruction(step?.instruction ?? "");
    setStepType(step?.stepType ?? "MANUAL");
    setComponent(step?.component ?? "");
    setMandatory(step?.mandatory ?? true);
    setEvidenceRequired(step?.evidenceRequired ?? false);
    setOnFailure(step?.onFailureAction ?? "STOP_RELEASE");
    setDuration(step?.plannedDurationMinutes ? String(step.plannedDurationMinutes) : "");
    setDependsOn("");
    setEnvironmentId(step?.environment?.id ?? leg?.id ?? release.targetEnvironment?.id ?? "");
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  const body = {
    phase,
    title: title.trim(),
    instruction: instruction.trim() === "" ? null : instruction.trim(),
    stepType,
    ownerId: null,
    environmentId: environmentId === "" ? null : environmentId,
    component: component.trim() === "" ? null : component.trim(),
    mandatory,
    evidenceRequired,
    onFailureAction: onFailure,
    plannedDurationMinutes: duration === "" ? null : Number(duration),
  };

  const submit = () =>
    void run(
      async () => {
        if (editing) {
          await api.patch(`/api/v1/releases/${release.id}/execution-steps/${step.id}`, {
            ...body,
            lockVersion: step.lockVersion,
          });
          return;
        }
        const created = await api.post<ReleaseExecutionStep>(
          `/api/v1/releases/${release.id}/execution-steps`,
          body,
        );
        if (dependsOn !== "") {
          await api.post(
            `/api/v1/releases/${release.id}/execution-steps/${created.id}/dependencies`,
            { dependsOnStepId: dependsOn },
          );
        }
      },
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? "Edit step" : "Add a step"}
      subtitle={editing ? step.title : "Something somebody does during the deployment."}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={title.trim() === ""} loading={saving}>
            {saving ? "Saving…" : editing ? "Save" : "Add step"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Phase" required>
            <Select
              value={phase}
              onChange={(event) => setPhase(event.target.value as ExecutionPhase)}
            >
              {Object.entries(EXECUTION_PHASE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Kind" required>
            <Select
              value={stepType}
              onChange={(event) => setStepType(event.target.value as ExecutionStepType)}
            >
              {STEP_TYPES.map((value) => (
                <option key={value} value={value}>
                  {value.toLowerCase()}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="What is being done" required>
          <Input
            autoFocus
            value={title}
            placeholder="Take a production database snapshot"
            onChange={(event) => setTitle(event.target.value)}
          />
        </Field>

        <Field
          label="Instructions"
          hint="Never a password, key or token — reference where the secret lives instead."
        >
          <Textarea
            rows={4}
            value={instruction}
            placeholder="pg_dump against prod-db-1 — use secret prod/payment-db from Vault."
            onChange={(event) => setInstruction(event.target.value)}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field
          label="Applies to"
          hint="Which leg of the journey this step belongs to. Steps for one environment do not hold up a deployment to another."
        >
          <Select
            value={environmentId}
            onChange={(event) => setEnvironmentId(event.target.value)}
          >
            <option value="">Every environment</option>
            {(environments.data ?? []).map((environment) => (
              <option key={environment.id} value={environment.id}>
                {environment.name} only
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Component" hint="What it touches.">
            <Input
              value={component}
              placeholder="PostgreSQL"
              onChange={(event) => setComponent(event.target.value)}
            />
          </Field>
          <Field label="Expected minutes">
            <Input
              type="number"
              min={0}
              value={duration}
              onChange={(event) => setDuration(event.target.value)}
            />
          </Field>
        </div>

        <Field
          label="If it fails"
          hint="Recorded as what should happen. Nothing is triggered automatically."
        >
          <Select
            value={onFailure}
            onChange={(event) => setOnFailure(event.target.value as OnFailureAction)}
          >
            {Object.entries(ON_FAILURE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>

        {!editing && allSteps.length > 0 && (
          <Field label="Waits for" hint="This step cannot start until that one is settled.">
            <Select value={dependsOn} onChange={(event) => setDependsOn(event.target.value)}>
              <option value="">Nothing — it can start any time</option>
              {allSteps.map((other) => (
                <option key={other.id} value={other.id}>
                  {other.sequenceNo}. {other.title}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-ink-800">
            <input
              type="checkbox"
              checked={mandatory}
              onChange={(event) => setMandatory(event.target.checked)}
            />
            Mandatory — the release is not finished until this is settled
          </label>
          <label className="flex items-center gap-2 text-sm text-ink-800">
            <input
              type="checkbox"
              checked={evidenceRequired}
              onChange={(event) => setEvidenceRequired(event.target.checked)}
            />
            Requires evidence — it cannot be completed without something attached
          </label>
        </div>
      </div>
    </Drawer>
  );
}
